import { styled } from '@linaria/react'
import React, {
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker'
import { createEnUSLocale, createRuLocale } from '../shared/utils/locales'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Switch } from '../ui/Switch'
import { InfoCard } from '../ui/InfoCard'
import { InfoHint } from '../ui/InfoHint'
import { CommissionControl } from './CommissionControl'
import { ButtonRegular } from '../ui/Button'
import { Input } from '../ui/Input'
import { ErrorText } from '../ui/ErrorText'
import { colors } from '../styles/colors'
import { Typography } from '../ui/Typography'
import { OptionInput } from './OptionInput'
import { useAppContext } from '../contexts/App.context'
import { Select } from '../ui/Select'
import PlusIcon from '../svgr/Plus'
import QuestionIcon from '../svgr/Faq'
import { getTimezoneSelectOptions } from '../shared/utils/timezones'
import 'react-datepicker/dist/react-datepicker.css'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import { UploadImageButton } from './UploadImageButton'
import { CreateBoxConfirmDialog } from '../dialogs/CreateBoxConfirmDialog'
import { formatPercentInBasisPoints } from '../functions/formatPercentInBasisPoints'
import { useCreateBox } from '../hooks/useCreateBox'
import { BottomSheet, useBottomSheet } from './BottomSheet/BottomSheet'
import { formatTon } from '../utils/formatTon'
import { HowItWorksContent } from './HowItWorksContent'

export type FormData = {
  question: string
  options: { value: string }[]
  deadline: Date | null
  commission: number
  timezone: string
  imageUrl: string | null
  publishToFeed: boolean
  multipleChoice: boolean
  quizMode: boolean
}

dayjs.extend(utc)
dayjs.extend(timezone)

export const NewBoxForm: FC<{
  className?: string
}> = ({ className }) => {
  const { language, boxCollection } = useAppContext()
  const { formatMessage } = useIntl()
  const { sendCreateBoxMessage } = useCreateBox()
  const simpleSheet = useBottomSheet()
  const howItWorksSheet = useBottomSheet()
  const [boxCreationFee, setBoxCreationFee] = useState<bigint | undefined>(
    undefined,
  )

  // Take boxCreationFee from contract
  useEffect(() => {
    boxCollection?.getSettings().then(data => {
      setBoxCreationFee(data.box_creation_fee)
    })
  }, [boxCollection])

  const handleNewBoxFormSubmit = useCallback(
    async (data: FormData) => {
      if (!data.deadline || !boxCreationFee) {
        // TODO: error?
        return
      }

      // TODO: it's better to calc endTimeInSeconds with timezone here, to avoid bugs when timezone was changed

      await sendCreateBoxMessage(
        {
          name: data.question.trim(),
          imageUrl: data.imageUrl,
          choices: data.options.map(option => option.value.trim()),
          endTimeInSeconds: BigInt(data.deadline.getTime() / 1000),
          creatorBasisPoints: BigInt(
            formatPercentInBasisPoints(Number(data.commission)),
          ),
          publishToFeed: data.publishToFeed,
        },
        boxCreationFee,
      )
    },
    [sendCreateBoxMessage, boxCreationFee],
  )

  const handleCreateButtonClick = async () => {
    const isFormValid = await trigger()

    if (isFormValid) {
      simpleSheet.open()
    }
  }

  const {
    control,
    formState: { isDirty, errors },
    handleSubmit,
    watch,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      question: '',
      options: [{ value: '' }, { value: '' }],
      deadline: null,
      commission: 5,
      timezone: 'UTC',
      publishToFeed: false,
      imageUrl: null,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const watchedTimezone = watch('timezone')

  // Set up dayjs and DatePicker locales
  useEffect(() => {
    dayjs.locale(language)
    registerLocale('ru', createRuLocale(formatMessage))
    registerLocale('en', createEnUSLocale(formatMessage))
    setDefaultLocale(language)
  }, [language, formatMessage])

  const watchedOptions = watch('options')

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options',
    rules: {
      minLength: {
        value: 2,
        message: formatMessage({
          id: 'newBox.form.optionsMinCount',
          defaultMessage: 'Минимум 2 варианта ответа',
        }),
      },
    },
  })

  const addOption = () => {
    append({ value: '' })
  }

  const removeOption = (index: number) => {
    if (fields.length > 2) {
      remove(index)
    }
  }

  const validateQuestionName = (value: string) => {
    if (!value) return true

    // Check if only contains allowed characters
    if (!/^[a-zA-Zа-яА-ЯёЁ0-9\s.,?]+$/.test(value)) {
      return formatMessage({
        id: 'newBox.form.questionAlphanumeric',
        defaultMessage: 'Только буквы, цифры, пробелы и знаки . , ?',
      })
    }

    // Check if starts with . or ,
    if (/^[.,]/.test(value)) {
      return formatMessage({
        id: 'newBox.form.questionInvalidStart',
        defaultMessage: 'Не может начинаться с точки или запятой',
      })
    }

    // Check if ends with ,
    if (/,$/.test(value)) {
      return formatMessage({
        id: 'newBox.form.questionInvalidEnd',
        defaultMessage: 'Не может заканчиваться запятой',
      })
    }

    // Check if ? appears anywhere except at the end
    const questionMarkIndex = value.indexOf('?')
    if (questionMarkIndex !== -1 && questionMarkIndex !== value.length - 1) {
      return formatMessage({
        id: 'newBox.form.questionMarkOnlyAtEnd',
        defaultMessage: 'Знак вопроса может быть только в конце',
      })
    }

    // Check if there are multiple ?
    if ((value.match(/\?/g) || []).length > 1) {
      return formatMessage({
        id: 'newBox.form.questionMarkOnlyAtEnd',
        defaultMessage: 'Знак вопроса может быть только в конце',
      })
    }

    return true
  }

  const validateUniqueOption = (value: string, index: number) => {
    if (!value.trim()) return true

    const currentValues = watchedOptions
      .map((option, i) => ({
        value: option.value.trim().toLowerCase(),
        index: i,
      }))
      .filter(item => item.index !== index && item.value !== '')

    const isDuplicate = currentValues.some(
      item => item.value === value.trim().toLowerCase(),
    )

    return isDuplicate
      ? formatMessage({
          id: 'newBox.form.optionDuplicate',
          defaultMessage: 'Такой вариант уже есть',
        })
      : true
  }

  return (
    <Container className={className}>
      <InfoCard icon={<StyledQuestionIcon />} onClick={howItWorksSheet.open}>
        <FormattedMessage
          id="newBox.howItWorks"
          defaultMessage="Как это работает?"
        />
      </InfoCard>

      <FormSection>
        <Controller
          name="question"
          control={control}
          rules={{
            required: formatMessage({
              id: 'newBox.form.questionRequired',
              defaultMessage: 'Вопрос голосования обязателен',
            }),

            minLength: {
              value: 5,

              message: formatMessage({
                id: 'newBox.form.questionMinLength',
                defaultMessage: 'Минимум 5 символов',
              }),
            },

            maxLength: {
              value: 200,

              message: formatMessage({
                id: 'newBox.form.questionMaxLength',
                defaultMessage: 'Максимум 200 символов',
              }),
            },

            validate: validateQuestionName,
          }}
          render={({ field }) => (
            <>
              <Input
                label={
                  <Typography fontSize={12} fontWeight={400} font="roboto">
                    {formatMessage({
                      id: 'newBox.form.questionLabel',
                      defaultMessage: 'Вопрос голосования',
                    })}
                  </Typography>
                }
                placeholder={formatMessage({
                  id: 'newBox.form.questionPlaceholder',
                  defaultMessage: 'Введите вопрос',
                })}
                {...field}
              />

              {errors.question && (
                <ErrorText>{errors.question.message}</ErrorText>
              )}
            </>
          )}
        />
      </FormSection>

      <FormSection>
        <SectionLabel>
          {formatMessage({
            id: 'newBox.form.optionsLabel',
            defaultMessage: 'Варианты ответа',
          })}
        </SectionLabel>

        {fields.map((field, index) => (
          <Controller
            key={field.id}
            name={`options.${index}.value`}
            control={control}
            rules={{
              required: formatMessage({
                id: 'newBox.form.optionRequired',
                defaultMessage: 'Вариант ответа не может быть пустым',
              }),
              minLength: {
                value: 1,
                message: formatMessage({
                  id: 'newBox.form.optionMinLength',
                  defaultMessage: 'Минимум 1 символ',
                }),
              },
              maxLength: {
                value: 100,
                message: formatMessage({
                  id: 'newBox.form.optionMaxLength',
                  defaultMessage: 'Максимум 100 символов',
                }),
              },
              validate: value => validateUniqueOption(value, index),
            }}
            render={({ field: inputField, fieldState }) => (
              <OptionInput
                {...inputField}
                onChange={inputField.onChange}
                placeholder={formatMessage({
                  id: 'newBox.form.optionPlaceholder',
                  defaultMessage: 'Введите вариант ответа',
                })}
                showRemove={fields.length > 2}
                onRemove={() => removeOption(index)}
                hasError={!!fieldState.error}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        ))}

        {errors.options && <ErrorText>{errors.options.message}</ErrorText>}

        <AddButton type="button" onClick={addOption}>
          <AddOptionButtonContent>
            <StyledPlusIcon />
            {formatMessage({
              id: 'newBox.addOption',
              defaultMessage: 'Добавить',
            })}
          </AddOptionButtonContent>
        </AddButton>
      </FormSection>

      <FormSection>
        <SectionLabel>
          {formatMessage({
            id: 'newBox.deadline',
            defaultMessage: 'Дедлайн',
          })}
        </SectionLabel>

        <Controller
          name="deadline"
          control={control}
          rules={{
            required: formatMessage({
              id: 'newBox.deadlineRequired',
              defaultMessage: 'Дедлайн обязателен',
            }),
          }}
          render={({ field }) => (
            <>
              <DatePickerWrapper>
                <DatePicker
                  selected={field.value}
                  onChange={(date: Date | null) => {
                    field.onChange(
                      date && watchedTimezone
                        ? dayjs(date).tz(watchedTimezone).toDate()
                        : date,
                    )
                  }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd.MM.yyyy HH:mm"
                  minDate={new Date()}
                  locale={language === 'ru' ? 'ru' : 'en'}
                  placeholderText={formatMessage({
                    id: 'newBox.deadlinePlaceholder',
                    defaultMessage: 'Выберите дату и время',
                  })}
                  wrapperClassName="date-picker-wrapper"
                  className="date-picker-input"
                />
              </DatePickerWrapper>

              {errors.deadline && (
                <ErrorText>{errors.deadline.message}</ErrorText>
              )}

              <TimezoneInfo>
                {watchedTimezone && field.value && (
                  <Typography fontSize={10} fontWeight={400} font="roboto">
                    {formatMessage(
                      {
                        id: 'newBox.timezoneInfo',
                        defaultMessage: 'Время в {timezone}: {time}',
                      },
                      {
                        timezone: watchedTimezone,
                        time: dayjs(field.value)
                          .tz(watchedTimezone)
                          .format('DD.MM.YYYY HH:mm'),
                      },
                    )}
                  </Typography>
                )}
              </TimezoneInfo>
            </>
          )}
        />

        <InfoHint>
          {formatMessage({
            id: 'newBox.timezoneHint',
            defaultMessage:
              'Учитывайте свой часовой пояс или выберите подходящий',
          })}
        </InfoHint>

        <Controller
          name="timezone"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={getTimezoneSelectOptions(formatMessage)}
              placeholder={formatMessage({
                id: 'newBox.timezonePlaceholder',
                defaultMessage: 'Выберите часовой пояс',
              })}
            />
          )}
        />
      </FormSection>

      <FormSection>
        <Controller
          name="commission"
          control={control}
          rules={{
            min: {
              value: 0,
              message: formatMessage({
                id: 'newBox.commissionMin',
                defaultMessage: 'Минимальная комиссия 0%',
              }),
            },
            max: {
              value: 99,
              message: formatMessage({
                id: 'newBox.commissionMax',
                defaultMessage: 'Максимальная комиссия 99%',
              }),
            },
          }}
          render={({ field }) => (
            <>
              <CommissionControl
                value={field.value}
                onChange={field.onChange}
                label={formatMessage({
                  id: 'newBox.commissionLabel',
                  defaultMessage: 'Комиссия блогера',
                })}
              />
              {errors.commission && (
                <ErrorText>{errors.commission.message}</ErrorText>
              )}
            </>
          )}
        />
      </FormSection>

      <FormSection>
        <SectionLabel>
          {formatMessage({
            id: 'newBox.form.imageLabel',
            defaultMessage: 'Изображение',
          })}
        </SectionLabel>

        <Controller
          name="imageUrl"
          control={control}
          render={({ field }) => (
            <>
              <UploadImageButton
                onImageSelect={url => field.onChange(url)}
                onImageRemove={() => field.onChange(null)}
              />
              {errors.imageUrl && (
                <ErrorText>{errors.imageUrl.message}</ErrorText>
              )}
            </>
          )}
        />
      </FormSection>

      <FormSection>
        <SectionLabel>
          {formatMessage({
            id: 'newBox.additionalLabel',
            defaultMessage: 'Дополнительно',
          })}
        </SectionLabel>

        <Controller
          name="publishToFeed"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={field.onChange}
              label={formatMessage({
                id: 'newBox.publishToFeed',
                defaultMessage: 'Выводить в ленту',
              })}
              description={formatMessage({
                id: 'newBox.publishToFeedDescription',
                defaultMessage: 'Дополнительная модерация',
              })}
            />
          )}
        />
      </FormSection>

      <SubmitButton
        onClick={handleCreateButtonClick}
        textColor={colors.white}
        textWeight={700}
        disabled={!isDirty}
      >
        {formatMessage({
          id: 'newBox.createButton',
          defaultMessage: 'Создать',
        })}
      </SubmitButton>

      <BottomSheet ref={simpleSheet.ref}>
        <CreateBoxConfirmDialog
          onConfirm={handleSubmit(handleNewBoxFormSubmit)}
          onClose={simpleSheet.close}
          data={{
            commission: `${formatTon(boxCreationFee)} TON`,
            earnings: `${watch('commission')}%`,
          }}
        />
      </BottomSheet>

      <BottomSheet ref={howItWorksSheet.ref}>
        <HowItWorksContent onClose={howItWorksSheet.close} />
      </BottomSheet>
    </Container>
  )
}

const SectionLabel = (props: Partial<ComponentProps<typeof Typography>>) => (
  <Typography fontSize={12} fontWeight={400} font="roboto" {...props} />
)

const Container = styled.form`
  display: grid;
  gap: 16px;
  background: ${colors.bgPrimary};
  width: 100%;
  margin: 0 auto;
`

const FormSection = styled.div`
  display: grid;
  gap: 6px;
`

const AddButton = styled(ButtonRegular)`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.secondary};
  height: 32px;
  padding: 8px 12px;
`

const SubmitButton = styled(ButtonRegular)`
  margin-top: 16px;
  width: 100%;
  background: ${colors.secondary};
  color: ${colors.white};
`

const DatePickerWrapper = styled.div`
  .date-picker-wrapper {
    width: 100%;
  }

  .date-picker-input {
    height: 42px;
    width: 100%;
    display: block;
    padding: 12px 16px;
    border-radius: 16px;
    border: 1px solid ${colors.borderPrimary};
    background-color: ${colors.inputBg};
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    outline: none;
    color: ${colors.inputText};
    box-sizing: border-box;
    transition: border-color 0.2s ease;

    &::placeholder {
      color: ${colors.inputPlaceholder};
    }

    &:focus {
      outline: none;
      border-color: ${colors.borderFocus};
    }

    &:hover {
      border-color: ${colors.borderHover};
    }
  }

  .react-datepicker {
    background: ${colors.bgPrimary};
    border: 1px solid ${colors.borderPrimary};
    border-radius: 12px;
    font-family: 'Roboto', sans-serif;
  }

  .react-datepicker__header {
    background: ${colors.bgSecondary};
    border-bottom: 1px solid ${colors.borderPrimary};
    border-radius: 12px 12px 0 0;
  }

  .react-datepicker__current-month,
  .react-datepicker-time__header {
    color: ${colors.textPrimary};
    font-weight: 500;
  }

  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    color: ${colors.textPrimary};
  }

  .react-datepicker__day:hover {
    background: ${colors.bgTertiary};
  }

  .react-datepicker__day--selected {
    background: ${colors.buttonPrimaryBg};
    color: ${colors.buttonPrimaryText};
  }

  .react-datepicker__day--keyboard-selected {
    background: ${colors.bgTertiary};
  }
`

const TimezoneInfo = styled.div`
  margin-top: 4px;
  color: ${colors.textSecondary};
`

const StyledQuestionIcon = styled(QuestionIcon)`
  width: 24px;
  height: 24px;
`

const StyledPlusIcon = styled(PlusIcon)`
  width: 16px;
  height: 16px;
`

const AddOptionButtonContent = styled.span`
  display: grid;
  align-items: center;
  justify-content: center;
  grid-auto-flow: column;
  gap: 4px;
`
